import type { CartCategoryOption } from "@/utils/cart";
import { memo, useCallback } from "react";
import { FilterSection } from "./FilterSection";

export interface CategoryFilterListProps {
  /** Categories represented by the products currently in the cart. */
  categories: CartCategoryOption[];
  /** IDs of currently selected categories (empty = no category filtering). */
  selectedCategoryIds: string[];
  /** Callback when toggling a category's selection */
  onToggleCategory: (categoryId: string) => void;
}

/**
 * Collapsible "Categories" filter section.
 *
 * Only lists categories actually present in the cart - unlike search, where
 * the facet spans the whole catalogue - so a salon never sees a category that
 * would filter their basket down to nothing.
 */
function CategoryFilterListComponent({
  categories,
  selectedCategoryIds,
  onToggleCategory
}: CategoryFilterListProps) {
  if (categories.length === 0) return null;

  return (
    <FilterSection title="Categories">
      {categories.map((category) => (
        <CategoryFilterItem
          key={category.id}
          categoryId={category.id}
          name={category.name}
          itemCount={category.itemCount}
          isChecked={selectedCategoryIds.includes(category.id)}
          onToggle={onToggleCategory}
        />
      ))}
    </FilterSection>
  );
}

interface CategoryFilterItemProps {
  categoryId: string;
  name: string;
  itemCount: number;
  isChecked: boolean;
  onToggle: (categoryId: string) => void;
}

const CategoryFilterItem = memo(function CategoryFilterItem({
  categoryId,
  name,
  itemCount,
  isChecked,
  onToggle
}: CategoryFilterItemProps) {
  const handleToggle = useCallback(() => {
    onToggle(categoryId);
  }, [categoryId, onToggle]);

  return (
    <label className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={isChecked}
        onChange={handleToggle}
        className="h-4 w-4 rounded border-gray-300"
      />
      <span className="flex-1">{name}</span>
      <span className="text-xs text-gray-400">({itemCount})</span>
    </label>
  );
});

export const CategoryFilterList = memo(CategoryFilterListComponent);
